import { moduleApi } from "@amtana/module";
import type { EraseStep } from "@amtana/module";

import type {
  CheckCodexSignInInput,
  CodexSignInCheck,
  FinishClaudeSignInInput,
  HarnessLogin,
  HarnessLogins,
  HarnessSignIn,
  HarnessTurnLogin,
  RemoveHarnessLoginInput,
  StartHarnessSignInInput,
} from "./harness-login.schemas.ts";
import type {
  AgentVaultSecrets,
  DeleteVaultSecretInput,
  ListVaultSecretsOutput,
  Ok,
  SaveVaultLoginInput,
  SaveVaultSecretInput,
  VaultRelease,
  VaultResolveCall,
  VaultResolveOutcome,
  VaultSecret,
  WebsiteLoginFill,
} from "./vault.schemas.ts";

/** Who asks, and where: secrets belong to the organization. */
export type ForMember = { readonly organizationId: string; readonly userId: string };

/** The agent itself, as its key resolved it at its own door. */
export type ForAgent = {
  readonly agentId: string;
  readonly organizationId: string;
  readonly ownerUserId: string;
};

// The promise modules/vault makes. A member saves, lists and deletes secrets; the value goes in
// once and never comes back out through any of them. `resolve` is the egress proxy's door
// (seam 3): the only way a value leaves, for an allowed agent, to an allowed host, during a live
// turn, with every unwrap in the audit log.
// Peer-only `eraseStep` (ADR-026, epic 30, S30.14, D927): the member's own logins and sign-ins
// everywhere; a sole organization's secrets and data key; a shared one's creator anonymized.
export interface VaultApi extends EraseStep {
  save(input: SaveVaultSecretInput & ForMember): Promise<VaultSecret>;
  /** A website login (D657); its value never comes back out through any door. */
  saveLogin(input: SaveVaultLoginInput & ForMember): Promise<VaultSecret>;
  list(input: { readonly organizationId: string }): Promise<ListVaultSecretsOutput>;
  delete(input: DeleteVaultSecretInput & { readonly organizationId: string }): Promise<Ok>;
  resolve(input: VaultResolveCall): Promise<VaultResolveOutcome>;
  /** `zaistev vault list` (S27.15, D750): the secrets allowed for this agent, by name,
   * placeholder and hosts (a website login by its site); never a value. */
  agentSecrets(input: ForAgent): Promise<AgentVaultSecrets>;
  /** `zaistev vault run` (ADR-023 §4, D601): the values of these secrets for one child process,
   * released only during a live turn of this agent (its turn token), only for secrets allowed for
   * it, all or none, each release audited. ADR-023 accepts that the child can read them. */
  release(
    input: ForAgent & { readonly turnToken: string | undefined; readonly names: readonly string[] },
  ): Promise<VaultRelease>;
  /**
   * Peer-only, for the agent runtime's `sign_in` (ADR-023 §4, D657): the website login of this
   * name (matched as its placeholder is) and, with a seed, the current one-time code, for the
   * runtime to type into the page itself. Only during a live turn of this agent (its turn token),
   * only when the login is allowed for it; each fill audited as the agent (`vault:fill`). Null
   * when the organization has no website login of that name: the member signs in by hand.
   */
  websiteLogin(
    input: ForAgent & { readonly turnToken: string | undefined; readonly name: string },
  ): Promise<WebsiteLoginFill | null>;

  // The member's own Claude or Codex login (ADR-023 §7, S27.06): one per harness per member,
  // kept as a `harness-login` secret, never listed with the organization's secrets and never
  // answered by `resolve`.

  /** The caller's own logins, without a token. */
  harnessLogins(input: ForMember): Promise<HarnessLogins>;
  /** Starts a sign-in: Claude's page to open, or OpenAI's device code. Starting again for the
   * same harness replaces the sign-in that was waiting. */
  startHarnessSignIn(input: StartHarnessSignInInput & ForMember): Promise<HarnessSignIn>;
  /** Exchanges the `code#state` Claude's page showed and keeps the token, replacing any login
   * the member had for Claude. */
  finishClaudeSignIn(input: FinishClaudeSignInInput & ForMember): Promise<HarnessLogin>;
  /** One check of a Codex sign-in; once OpenAI says approved, exchanges the code, keeps the
   * `auth.json` and answers signed in. */
  checkCodexSignIn(input: CheckCodexSignInInput & ForMember): Promise<CodexSignInCheck>;
  removeHarnessLogin(input: RemoveHarnessLoginInput & ForMember): Promise<Ok>;
  /**
   * Peer-only, for the agent runtime as it delivers a task (seam 6): the login this turn runs
   * on, for the agent the turn token names and only that one, its owner's own for its harness.
   * Audited as released (`vault:login_release`, as the agent on its owner's behalf). Refused as
   * `harness_login_missing` when the owner has none, `vault_turn_invalid` for a token that is
   * not this agent's, `vault_turn_expired` for a turn that is over.
   */
  harnessLoginForTurn(input: {
    readonly agentId: string;
    readonly turnToken: string;
  }): Promise<HarnessTurnLogin>;
  /** `PUT /api/agents/me/codex-login`: the `auth.json` Codex refreshed during a turn of this
   * agent, kept as its owner's Codex login; refused for another account or another agent's turn. */
  keepRefreshedCodexLogin(
    input: ForAgent & { readonly turnToken: string | undefined; readonly authJson: string },
  ): Promise<Ok>;
}

export const VaultApi = moduleApi<VaultApi>()("vault");
